import { Component } from "react";
import Immutable from "immutable";
import { connect } from "react-redux";
import { Field, reduxForm, formValueSelector } from "redux-form/immutable";
import { Button, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";

import * as EntryActions from "../../actions/entry";

import { ResponsiveDialog } from "../modal";
import { numberOrEmpty, plainInput } from "../forms";
import { LoadingButton } from "../actions/loadingButton";
import { AthleteDetails, mapProperties } from "../layout/dialogs/athlete/AthleteDetails";
import { AthleteRegistrationDetails } from "../layout/dialogs/athlete/AthleteRegistrationDetails";
import { TeamMembers } from "components/layout/dialogs/team/TeamMembers";
import { T } from "../util/t";
import { EntryStatusPill } from "components/layout/entries/EntryStatusPill";
import { AsyncTeamSelectorField } from "components/layout/forms/fields/SelectorField";
import { FormFieldWrapper } from "components/layout/forms/FormField";

const formName = "editEntry";
const selector = formValueSelector(formName);

export const EntryDialog = ({ open, onClose, ...props }) =>
    <ResponsiveDialog open={open} className="lh-dialog"
                      fullWidth={true}
                      maxWidth="sm"
                      onClose={onClose}
                      aria-labelledby="entry-dialog-title"
                      aria-describedby="entry-dialog-description">
        <EntryDialogContent {...props} onClose={onClose}/>
    </ResponsiveDialog>;

export const EntryDialogContent = connect((state, { entry }) => ({
    initialValues: Immutable.Map({
        status: entry.get("status"),
        seed: entry.get("seed"),
        bib: entry.get("bib"),
        team_name: entry.get("team_name"),
        properties: entry.get("properties"),
        team_members_attributes: entry.get("team_members")
    }),
    teamMembersFormValue: selector(state, "team_members_attributes") || Immutable.List(),
}))(reduxForm({ form: formName, enableReinitialize: true, keepDirtyOnReinitialize: true, updateUnregisteredFields: true })(
    class EntryDialogContent extends Component {
        submit = values => {
            const { dispatch, entry, eventDivisionId, onClose } = this.props;
            return dispatch(EntryActions.updateEntry(eventDivisionId, entry.get("id"), values)).then(onClose);
        };

        onTeamSelected = value => {
            if(typeof value?.value === "number"){
                this.props.change("team_id", value?.value || null);
            }
            this.props.change("team_name", value?.label || null);
        };

        render = () => {
            const { entry, eventDivisionId, onClose, removeButton, showBib, showSeed, seedIsPosition, pristine, handleSubmit, isTeams, captureProperties } = this.props;
            const [athleteProperties, registrationProperties, userProperties] = captureProperties.reduce(([aPs, rPs, uPs], p) => {
                if (p.level === "athlete") aPs.push(p);
                if (p.level === "registration") rPs.push(p);
                if (p.level === "user") uPs.push(p);
                return [aPs, rPs, uPs];
            }, [[], [], []]);
            const entryPropertyMapper = mapProperties(entry);
            const payments = entry.getIn(["payment", "id"]) && entry.getIn(["payment", "status"]) === "success" && [{ id: entry.getIn(["payment", "id"]), intent_id: entry.getIn(["payment", "intent_id"]), direct_charge: entry.getIn(["payment", "direct_charge"]) }];

            return (
                <>
                    <div className="scrollable-area">
                        <DialogTitle id="entry-dialog-title" className="sticky-dialog-title">
                            <div className="title-top">
                                <Typography variant="h3">{entry.getIn(["athlete", "name"])}</Typography>
                                <EntryStatusPill status={"published"}>{entry.get("status")}</EntryStatusPill>
                            </div>
                        </DialogTitle>

                        <DialogContent id="entry-dialog-description" className="sticky-dialog-content">
                            <form onSubmit={handleSubmit(this.submit)}>
                                {showSeed &&
                                <Field name={"seed"} component={plainInput} pattern="[0-9]*" maxLength="10"
                                       label={<><T>{seedIsPosition ? "Position" : "Seed"}</T>:</>}
                                       placeholder={entry.get("rank")} validate={numberOrEmpty}/>}

                                {showBib &&
                                <Field name={"bib"} component={plainInput} maxLength="10" label={<><T>Bib</T>:</>}/>}
                                <FormFieldWrapper label="Team:">
                                    <AsyncTeamSelectorField
                                        autocompleteProps={{ blurOnSelect: true }}
                                        inputProps={{ autoFocus: false }}
                                        value={entry.get("team_name")}
                                        onChange={this.onTeamSelected}
                                    />
                                </FormFieldWrapper>

                                {isTeams && (
                                    <TeamMembers
                                        array={this.props.array}
                                        change={this.props.change}
                                        members={this.props.teamMembersFormValue.toJS()}
                                        teamRoles={this.props.teamRoles}
                                    />
                                )}

                                <AthleteDetails
                                    athlete={entry.get("athlete")}
                                    athleteProperties={athleteProperties}
                                    userProperties={userProperties}
                                    title={isTeams && "Team details"}
                                    isTeams={isTeams}
                                />

                                <AthleteRegistrationDetails
                                    title="Registration details"
                                    entryId={entry.get("id")}
                                    eventDivisionId={eventDivisionId}
                                    entryDate={entry.get("entry_date")}
                                    cancelledDate={entry.get("cancelled_date")}
                                    payments={payments}
                                    registrationProperties={registrationProperties.map(entryPropertyMapper)}
                                    captureProperties={captureProperties}
                                    basePath={"properties"}
                                />
                            </form>
                        </DialogContent>
                    </div>
                    <DialogActions className="sticky-dialog-actions">
                        {removeButton}

                        <Button variant="outlined" onClick={onClose}>
                            <T>Cancel</T>
                        </Button>

                        <LoadingButton variant="contained" disabled={pristine} color="primary" type="submit" action={handleSubmit(this.submit)}>
                            <T>Save</T>
                        </LoadingButton>
                    </DialogActions>
                </>
            );
        };
    }
));
